export type TermValue =
  string | number | boolean | { value: string | number | boolean; _name?: string; boost?: number };
export type TermClause = { term: Record<string, TermValue> };
export type TermsClause = { terms: Record<string, Array<string | number>> };
export type ExistsClause = { exists: { field: string } };
export type RangeClause = {
  range: Record<
    string,
    { gte?: string | number; lte?: string | number; gt?: string | number; lt?: string | number }
  >;
};
export type BoolClause = {
  bool: {
    must?: FilterClause[];
    filter?: FilterClause[];
    should?: FilterClause[];
    must_not?: FilterClause[];
    minimum_should_match?: number | string;
    boost?: number;
  };
};
export type MultiMatchClause = {
  multi_match: {
    query: string;
    fields: string[];
    operator?: 'and' | 'or';
    type?: 'best_fields' | 'most_fields' | 'cross_fields' | 'phrase' | 'phrase_prefix';
    fuzziness?: 'AUTO' | number | string;
    lenient?: boolean;
    boost?: number;
    /** Named query — surfaced per-hit as `matched_queries` for match attribution. */
    _name?: string;
  };
};
export type MatchClause = {
  match: Record<
    string,
    | string
    | {
        query: string;
        operator?: 'and' | 'or';
        fuzziness?: 'AUTO' | number | string;
        boost?: number;
        /** Named query — surfaced per-hit as `matched_queries` for match attribution. */
        _name?: string;
      }
  >;
};
export type PrefixClause = {
  prefix: Record<
    string,
    | string
    | {
        /** Matched verbatim against index terms — normalize to the target field's mapping (analyzed text indexes lowercase). */
        value: string;
        boost?: number;
        /** Named query — surfaced per-hit as `matched_queries` for match attribution. */
        _name?: string;
      }
  >;
};
export type WildcardClause = {
  wildcard: Record<string, string>;
};
export type MatchPhrasePrefixClause = {
  match_phrase_prefix: Record<string, string>;
};
/**
 * Returns the nested documents that actually matched, instead of leaving the
 * caller to re-filter the parent's whole nested array. `_source` include paths
 * resolve from the document root (`path.field`, not `field`) — a relative name
 * yields an empty `_source` with no error.
 */
export type InnerHitsOptions = {
  /** Response key for the matches. The API camelizes it, so prefer a single lowercase word. */
  name?: string;
  size?: number;
  from?: number;
  _source?: string[] | boolean;
};

/**
 * OpenSearch `nested` query — matches objects in a nested field, scored
 * independently. `query` is any leaf/bool clause scoped to the nested `path`
 * (e.g. a `bool` that filters `employees_nested.*` to a single company).
 */
export type NestedClause = {
  nested: {
    path: string;
    query: FilterClause;
    score_mode?: 'avg' | 'sum' | 'min' | 'max' | 'none';
    ignore_unmapped?: boolean;
    inner_hits?: InnerHitsOptions;
  };
};
export type FilterClause =
  | MultiMatchClause
  | MatchClause
  | PrefixClause
  | WildcardClause
  | MatchPhrasePrefixClause
  | TermClause
  | TermsClause
  | ExistsClause
  | RangeClause
  | BoolClause
  | NestedClause;
export type SortDirection = 'asc' | 'desc';
export type SortOptions = {
  order: SortDirection;
  missing?: '_first' | '_last' | string | number;
  unmapped_type?: string;
  mode?: 'min' | 'max' | 'avg' | 'sum' | 'median';
  // nested?: { path: string; filter?: FilterClause }; // enable if you need nested sorts
};

export type SortClause = Record<string, SortDirection | SortOptions>;

// Aggregation types
export type TermsAggregation = {
  terms: {
    field: string;
    size?: number;
    order?: Record<string, SortDirection> | Record<string, SortDirection>[];
  };
};

export type TopHitsAggregation = {
  top_hits: {
    size?: number;
    _source?: {
      includes?: string[];
      excludes?: string[];
    };
    sort?: SortClause[];
  };
};

export type SumAggregation = {
  sum: {
    field: string;
  };
};

export type RangeAggregationBucket = {
  key: string;
  from?: number;
  to?: number;
};

export type RangeAggregation = {
  range: {
    field: string;
    ranges: RangeAggregationBucket[];
  };
};

export type AggregationClause =
  | TermsAggregation
  | TopHitsAggregation
  | SumAggregation
  | RangeAggregation
  | { [key: string]: unknown };

export type AggregationDefinition = {
  [key: string]: AggregationClause & {
    aggs?: { [key: string]: AggregationDefinition[string] };
  };
};

/**
 * Maximum number of search tokens sent to OpenSearch when searching across
 * wildcard (`*`) fields. Limits combinatorial explosion — each token generates
 * a separate multi_match clause evaluated against every indexed field. Six
 * tokens is a pragmatic cap that covers most natural-language queries while
 * keeping cluster load reasonable. Increase cautiously; each additional token
 * linearly increases the number of sub-queries.
 */
const MAX_WILDCARD_SEARCH_TOKENS = 6;

export class OpenSearchQueryBuilder {
  private filters: FilterClause[] = [];
  private shouldClauses: FilterClause[] = [];
  /**
   * OR-combined search clauses placed under `bool.must` (with
   * minimum_should_match: 1). Use for type-routed search where each token may
   * match any of several named clauses. Takes precedence over
   * setSearchTokens/setSearchTerm when present.
   */
  private searchShouldClauses: FilterClause[] = [];
  /**
   * Filters applied AFTER aggregations (OpenSearch `post_filter`). Narrows the
   * returned hits without affecting the aggregation buckets — use for a facet
   * value that should still list its siblings after one is selected (e.g. the
   * company facet derived from search results).
   */
  private postFilters: FilterClause[] = [];
  private minimumShouldMatch?: number;
  private aggregations: AggregationDefinition = {};
  private selectedColumns?: string[];

  private sort: SortClause[] = [{ _score: 'desc' }];

  private from = 0;
  private size = 25;
  private searchAfterValues?: unknown[];

  private searchTerm?: string;
  private searchFields?: string[];
  private searchOptions?: { fuzziness?: 'AUTO' | number };

  private highlightFields = ['*'];

  private searchTokens?: string[];

  /** Deduplicates and trims search field names, returning undefined if empty. */
  private normalizeSearchFields(fields?: string[]): string[] | undefined {
    const cleaned = (fields || []).map((field) => field.trim()).filter(Boolean);

    if (cleaned.length === 0) {
      return undefined;
    }

    return [...new Set(cleaned)];
  }

  /** Splits a search term on whitespace and deduplicates tokens. */
  private tokenizeSearchTerm(term: string): string[] {
    const tokens = term
      .split(/\s+/)
      .map((token) => token.trim())
      .filter(Boolean);

    return [...new Set(tokens)];
  }

  /**
   * Builds a multi_match clause for a single query term against the given fields.
   *
   * When `fields` includes the wildcard `*`, fuzziness is automatically removed.
   * OpenSearch expands `*` to every indexed field, and applying fuzziness across
   * hundreds of fields causes severe performance degradation (cluster timeouts
   * under load). Exact-prefix matching on wildcard searches is a deliberate
   * trade-off: broader field coverage compensates for the loss of typo tolerance.
   */
  private buildMultiMatchClause(query: string, fields: string[]): MultiMatchClause {
    const searchOptions = { ...(this.searchOptions || {}) };

    if (fields.includes('*') && 'fuzziness' in searchOptions) {
      delete searchOptions.fuzziness;
    }

    return {
      multi_match: {
        query,
        fields,
        operator: 'or',
        type: 'best_fields',
        lenient: true,
        ...searchOptions,
      },
    };
  }

  /**
   * Sets pre-tokenized search terms. Each token generates a separate
   * multi_match clause combined with `should` (OR). Wildcard searches
   * are capped at {@link MAX_WILDCARD_SEARCH_TOKENS} tokens.
   */
  setSearchTokens(tokens: string[], fields?: string[], options?: { fuzziness?: 'AUTO' | number }) {
    this.searchTokens = tokens.filter(Boolean);
    this.searchFields =
      fields && fields.length > 0 ? this.normalizeSearchFields(fields) : this.searchFields;
    this.searchOptions = { ...this.searchOptions, ...options };
    return this;
  }

  /** Search term + multi_match */
  setSearchTerm(term: string, fields?: string[], options?: { fuzziness?: 'AUTO' | number }) {
    this.searchTerm = term;
    this.searchFields = this.normalizeSearchFields(fields);
    this.searchOptions = options;
    return this;
  }

  /** Adds a simple term filter if value is defined */
  mustTerm(field: string, value: string | number | boolean | undefined | null) {
    const isMeaningful =
      value !== undefined && value !== null && value !== '' && !(value === false); // adjust this if you want to allow `false` in specific fields

    if (isMeaningful) {
      this.filters.push({ term: { [field]: value } });
    }

    return this;
  }

  /** Adds a terms filter if array is present and non-empty */
  mustTerms(field: string, values: unknown[]) {
    if (Array.isArray(values) && values.length > 0) {
      this.filters.push({ terms: { [field]: values as Array<string | number> } });
    }
    return this;
  }

  /**
   * Adds a post_filter term (narrows hits but not aggregations) if value is
   * meaningful. Use for a facet value whose sibling buckets should remain
   * available after selection.
   */
  postFilterTerm(field: string, value: string | number | boolean | undefined | null) {
    const isMeaningful = value !== undefined && value !== null && value !== '' && value !== false;
    if (isMeaningful) {
      this.postFilters.push({ term: { [field]: value } });
    }
    return this;
  }

  /**
   * Adds an arbitrary post_filter clause. Use when a facet selection needs a
   * composed clause (bool/should, term:false, …) rather than a simple term.
   */
  addPostFilter(clause: FilterClause) {
    this.postFilters.push(clause);
    return this;
  }

  /** Handles either term or terms intelligently */
  mustTermFlexible(field: string, values?: (string | number)[]) {
    if (!values || values.length === 0) return this;

    if (values.length === 1) {
      this.filters.push({ term: { [field]: values[0] || '' } });
    } else {
      this.filters.push({ terms: { [field]: values } });
    }

    return this;
  }

  mustBool(field: string, value: boolean | undefined | null) {
    if (value === true || value === false) {
      this.filters.push({ term: { [field]: value } });
    }
    return this;
  }

  /**
   * Adds a match clause for full-text search on analyzed fields.
   *
   * Intentionally skips falsy values (empty string, null, undefined) so callers
   * don't need to guard before calling.
   */
  mustMatch(field: string, query: string) {
    if (query) {
      this.filters.push({ match: { [field]: query } });
    }
    return this;
  }

  /** Adds a range clause */
  range(field: string, start?: string, end?: string) {
    const hasStart = !!start;
    const hasEnd = !!end;

    if (!hasStart && !hasEnd) return this;

    this.filters.push({
      range: {
        [field]: {
          ...(hasStart ? { gte: start } : {}),
          ...(hasEnd ? { lte: end } : {}),
        },
      },
    });

    return this;
  }

  /** Adds a range clause with explicit operators (gt, gte, lt, lte) */
  rangeOp(field: string, op: 'gt' | 'gte' | 'lt' | 'lte', value: string | number) {
    this.filters.push({
      range: { [field]: { [op]: value } },
    });
    return this;
  }

  /** Adds a must_not term filter (field != value) */
  mustNotTerm(field: string, value: string | number | boolean) {
    this.filters.push({
      bool: { must_not: [{ term: { [field]: value } }] },
    });
    return this;
  }

  /** Adds a must_not terms filter (field not in values) */
  mustNotTerms(field: string, values: Array<string | number>) {
    if (values.length > 0) {
      this.filters.push({
        bool: { must_not: [{ terms: { [field]: values } }] },
      });
    }
    return this;
  }

  /** Adds a must_not exists filter (field is null/missing) */
  mustNotExist(field: string) {
    this.filters.push({
      bool: { must_not: [{ exists: { field } }] },
    });
    return this;
  }

  /** Adds a prefix filter */
  mustPrefix(field: string, value: string) {
    if (value) {
      this.filters.push({ prefix: { [field]: value } });
    }
    return this;
  }

  /** Adds a wildcard filter */
  mustWildcard(field: string, value: string) {
    if (value) {
      this.filters.push({ wildcard: { [field]: value } });
    }
    return this;
  }

  /** Adds an exists clause */
  exists(field: string) {
    this.filters.push({ exists: { field } });
    return this;
  }

  /** Wraps logic in a condition check */
  when(condition: unknown, fn: (builder: this) => void) {
    if (condition) fn(this);
    return this;
  }

  /** Adds an arbitrary filter */
  addFilter(filter: FilterClause) {
    this.filters.push(filter);
    return this;
  }

  /** Convenience method to add a filter if the condition is met */
  addFilterIf(condition: unknown, clause: FilterClause) {
    if (condition) {
      this.addFilter(clause);
    }
    return this;
  }

  /** Adds a should clause (single group) */
  addShould(clause: FilterClause) {
    this.shouldClauses.push(clause);
    return this;
  }

  /**
   * Adds a clause to the type-routed search group (OR-combined under
   * `bool.must` with minimum_should_match: 1). Use one call per candidate
   * field; tag clauses with `_name` to surface match attribution.
   */
  addSearchShould(clause: FilterClause) {
    this.searchShouldClauses.push(clause);
    return this;
  }

  /** Adds multiple OR groups */
  addShouldGroup(clauses: FilterClause[]) {
    this.shouldClauses.push(...clauses);
    return this;
  }

  /** Adds a nested should: [ [A], [B] ] as OR block */
  shouldEach(groups: FilterClause[][]) {
    groups.forEach((clauses) => {
      this.shouldClauses.push({ bool: { must: clauses } });
    });
    return this;
  }

  setMinimumShouldMatch(value: number) {
    this.minimumShouldMatch = value;
    return this;
  }

  setPagination(page: number, limit: number) {
    this.from = (page - 1) * limit;
    this.size = limit;
    return this;
  }

  /**
   * Use search_after for deep pagination beyond 10,000 results.
   * When set, `from` is omitted from the query (search_after replaces offset).
   * Pass the `searchAfter` array returned from the previous response.
   * Pass undefined or an empty array to clear and revert to offset pagination.
   */
  setSearchAfter(values: unknown[] | undefined) {
    this.searchAfterValues = values && values.length > 0 ? values : undefined;
    return this;
  }

  /**
   * @param sortBy Ordered tokens, as an array or comma-separated string. Only
   * trailing :asc/:desc or .asc/.desc are directions; a leading minus also
   * selects descending order for undotted paths.
   * @param opts Controls missing values, unmapped-field handling and the _id tiebreaker.
   * @returns This builder with its previous sort order replaced.
   */
  setSort(
    sortBy: string | string[],
    opts: {
      addIdTiebreaker?: boolean;
      defaultMissing?: '_last' | '_first' | 'auto';
      defaultUnmappedType?: string;
    } = {}
  ) {
    const { addIdTiebreaker = true, defaultMissing = 'auto', defaultUnmappedType } = opts;

    const parseOne = (token: string): { field: string; dir: SortDirection } => {
      let field = token.trim();
      let sortDirection: SortDirection = 'asc';

      const suffix = field.match(/[.:](asc|desc)$/i);
      if (suffix) {
        field = field.slice(0, -suffix[0].length);
        sortDirection = suffix[1]?.toLowerCase() === 'desc' ? 'desc' : 'asc';
      } else if (!field.includes('.') && field.startsWith('-')) {
        sortDirection = 'desc';
        field = field.slice(1);
      }

      return { field: field, dir: sortDirection };
    };

    const tokens = Array.isArray(sortBy)
      ? sortBy
      : `${sortBy}`
          .split(',')
          .map((s) => s.trim())
          .filter(Boolean);

    const sorts: SortClause[] = tokens.map((t) => {
      const { field, dir } = parseOne(t);

      // allow special fields to pass through simply
      if (field === '_score' || field === '_doc') {
        return { [field]: dir };
      }

      const missing =
        defaultMissing === 'auto' ? (dir === 'asc' ? '_first' : '_last') : defaultMissing;

      return {
        [field]: {
          order: dir,
          missing,
          ...(defaultUnmappedType ? { unmapped_type: defaultUnmappedType } : {}),
        },
      };
    });

    if (addIdTiebreaker) sorts.push({ _id: 'asc' });

    this.sort = sorts.length ? sorts : [{ _score: 'desc' }, { _id: 'asc' }];
    return this;
  }

  setHighlight(fields: string[]) {
    this.highlightFields = fields;
    return this;
  }

  isEmpty() {
    return (
      !this.searchTerm &&
      this.searchShouldClauses.length === 0 &&
      this.filters.length === 0 &&
      this.postFilters.length === 0
    );
  }

  /**
   * Select source fields to include in the OpenSearch _source response.
   *
   * Accepts snake_case field paths as provided by the /fields API `path` property.
   * The backend is the single source of truth for field path casing — callers
   * should pass `FieldOption.path` (snake_case) directly rather than deriving
   * paths from camelCase names on the frontend.
   *
   * @param sourcePaths - snake_case field paths (e.g., from FieldOption.path)
   * @param essentialFields - Additional fields to always include (optional)
   * @returns this for chaining
   *
   * @example
   * ```
   * new OpenSearchQueryBuilder()
   *   .selectSourceFields(['employee_name', 'tas_application_id'], ['id'])
   * ```
   */
  selectSourceFields(sourcePaths: string[], essentialFields: string[] = []): this {
    if (!sourcePaths || sourcePaths.length === 0) {
      this.selectedColumns = undefined;
      return this;
    }

    // Combine with essential fields and deduplicate
    this.selectedColumns = [...new Set([...sourcePaths, ...essentialFields])];

    return this;
  }

  /**
   * Add a terms aggregation wrapped in a `filter` aggregation — the cross-facet
   * counting pattern: pass every OTHER facet group's selection as `filters` so
   * this group's buckets reflect those while its own selection (applied via
   * post_filter) doesn't collapse its siblings. Buckets land at
   * `<name>.values.buckets` (read with getFilteredTermsAggregation). Name in
   * camelCase — the API deep-camelizes response keys.
   */
  addFilteredTermsAggregation(
    name: string,
    field: string,
    filters: FilterClause[],
    size: number = 50
  ): this {
    this.aggregations[name] = {
      filter: { bool: { filter: filters } },
      aggs: { values: { terms: { field, size } } },
    };
    return this;
  }

  /**
   * Add a named-buckets `filters` aggregation wrapped in a `filter` aggregation
   * (same cross-facet pattern). Use for facet groups whose buckets are composed
   * clauses rather than the values of one field (e.g. a 3-way enrollment split).
   * Counts land at `<name>.values.buckets.<key>.doc_count` (read with
   * getFilteredNamedFiltersAggregation). Use camelCase keys.
   */
  addFilteredNamedFiltersAggregation(
    name: string,
    filters: FilterClause[],
    buckets: Record<string, FilterClause>
  ): this {
    this.aggregations[name] = {
      filter: { bool: { filter: filters } },
      aggs: { values: { filters: { filters: buckets } } },
    };
    return this;
  }

  /**
   * The nested-path sibling of `addFilteredTermsAggregation`. Facets over a
   * field inside a `nested` mapping (e.g. `employees_nested.jurisdictions_keyword`),
   * with `filters` applied INSIDE the nested scope so the count reflects the
   * matching nested objects rather than the parent documents.
   *
   * `field` is the full dotted path; `path` its nested root. Buckets land at
   * `<name>.scoped.values.buckets` (read with getNestedFilteredTermsAggregation).
   *
   * Callers scoping a facet to one tenant MUST include that tenant's clause in
   * `filters` — a nested agg with no inner filter aggregates every nested object
   * on every matching parent, which on a multi-company person document means
   * counting other companies' entries.
   *
   * `options.include` restricts the buckets to an allowlist of values in the
   * QUERY. Where the field holds values a viewer may not see, filtering the
   * response client-side is too late — the disallowed keys and their counts
   * have already crossed the wire.
   */
  addNestedFilteredTermsAggregation(
    name: string,
    path: string,
    field: string,
    filters: FilterClause[],
    size: number = 50,
    options: { include?: string[] } = {}
  ): this {
    this.aggregations[name] = {
      nested: { path },
      aggs: {
        scoped: {
          filter: { bool: { filter: filters } },
          aggs: {
            values: {
              terms: { field, size, ...(options.include && { include: options.include }) },
            },
          },
        },
      },
    };
    return this;
  }

  /**
   * The nested-path sibling of `addFilteredNamedFiltersAggregation` — composed
   * buckets (rather than one field's values) counted inside a `nested` scope.
   * Counts land at `<name>.scoped.values.buckets.<key>.doc_count` (read with
   * getNestedFilteredNamedFiltersAggregation). Same tenant-scoping obligation
   * as addNestedFilteredTermsAggregation.
   */
  addNestedFilteredNamedFiltersAggregation(
    name: string,
    path: string,
    filters: FilterClause[],
    buckets: Record<string, FilterClause>
  ): this {
    this.aggregations[name] = {
      nested: { path },
      aggs: {
        scoped: {
          filter: { bool: { filter: filters } },
          aggs: { values: { filters: { filters: buckets } } },
        },
      },
    };
    return this;
  }

  /** Add a terms aggregation with optional sort order */
  addTermsAggregation(
    name: string,
    field: string,
    size: number = 10000,
    order?: Record<string, SortDirection>
  ): this {
    const termsConfig: TermsAggregation['terms'] = {
      field,
      size,
    };

    if (order) {
      termsConfig.order = order;
    }

    this.aggregations[name] = {
      terms: termsConfig,
    };
    return this;
  }

  /** Add a top hits aggregation */
  addTopHitsAggregation(name: string, size: number = 1, includes?: string[]): this {
    this.aggregations[name] = {
      top_hits: {
        size,
        ...(includes && {
          _source: {
            includes,
          },
        }),
      },
    };
    return this;
  }

  /** Add a sum aggregation (for summing numeric field values) */
  addSumAggregation(name: string, field: string): this {
    this.aggregations[name] = {
      sum: {
        field,
      },
    };
    return this;
  }

  /** Add a range aggregation (for bucketed numeric ranges) */
  addRangeAggregation(name: string, field: string, ranges: Array<RangeAggregationBucket>): this {
    this.aggregations[name] = {
      range: {
        field,
        ranges,
      },
    };
    return this;
  }

  /** Add a nested aggregation to an existing aggregation */
  addNestedAggregation(
    parentName: string,
    childName: string,
    aggregation: AggregationClause
  ): this {
    if (!this.aggregations[parentName]) {
      throw new Error(`Parent aggregation ${parentName} does not exist`);
    }

    if (!this.aggregations[parentName].aggs) {
      this.aggregations[parentName].aggs = {};
    }

    this.aggregations[parentName].aggs![childName] = aggregation;
    return this;
  }

  /** Builds final OpenSearch query */
  build() {
    const boolQuery: Record<string, unknown> = {};

    // --- Type-routed search (named clauses via addSearchShould) ---
    // Highest priority: any clause may match (minimum_should_match: 1).
    if (this.searchShouldClauses.length) {
      boolQuery.must = {
        bool: { should: this.searchShouldClauses, minimum_should_match: 1 },
      };
    }
    // --- Tokenized OR search (pre-tokenized input via setSearchTokens) ---
    else if (this.searchTokens?.length && this.searchFields?.length) {
      // Tokens are already deduped in tokenizeSearchTerm; trim for safety only
      const tokens = this.searchTokens.map((token) => token.trim()).filter(Boolean);
      const isWildcardSearch = this.searchFields.includes('*');
      const cappedTokens = isWildcardSearch ? tokens.slice(0, MAX_WILDCARD_SEARCH_TOKENS) : tokens;

      const tokenQueries = cappedTokens.map((token) =>
        this.buildMultiMatchClause(token, this.searchFields as string[])
      );

      // any token can match
      boolQuery.must = { bool: { should: tokenQueries, minimum_should_match: 1 } };
    }
    // --- String-based search path (via setSearchTerm) with per-token OR ---
    else if (this.searchTerm && this.searchFields?.length) {
      const normalizedSearchTerm = this.searchTerm.trim();
      const isWildcardSearch = this.searchFields.includes('*');

      if (isWildcardSearch) {
        const tokens = this.tokenizeSearchTerm(normalizedSearchTerm).slice(
          0,
          MAX_WILDCARD_SEARCH_TOKENS
        );

        if (tokens.length > 1) {
          const tokenQueries = tokens.map((token) =>
            this.buildMultiMatchClause(token, this.searchFields as string[])
          );

          boolQuery.must = {
            bool: {
              should: tokenQueries,
              minimum_should_match: 1,
            },
          };
        } else {
          boolQuery.must = this.buildMultiMatchClause(
            tokens[0] || normalizedSearchTerm,
            this.searchFields
          );
        }
      } else {
        boolQuery.must = this.buildMultiMatchClause(normalizedSearchTerm, this.searchFields);
      }
    }

    if (this.filters.length > 0) {
      boolQuery.filter = this.filters;
    }

    if (this.shouldClauses.length > 0) {
      boolQuery.should = this.shouldClauses;
      if (this.minimumShouldMatch !== undefined) {
        boolQuery.minimum_should_match = this.minimumShouldMatch;
      }
    }

    const query: Record<string, unknown> = { bool: boolQuery };

    const result: Record<string, unknown> = {
      query,
      size: this.size,
      sort: this.sort,
      ...(this.searchAfterValues && this.searchAfterValues.length > 0
        ? { search_after: this.searchAfterValues }
        : { from: this.from }),
      ...(this.highlightFields && {
        highlight: {
          fields: Object.fromEntries(this.highlightFields.map((f) => [f, {}])),
          fragment_size: 0,
        },
      }),
    };

    // Apply selected columns to _source if specified
    if (this.selectedColumns && this.selectedColumns.length > 0) {
      result._source = this.selectedColumns;
    }

    // Add aggregations if any exist
    if (Object.keys(this.aggregations).length > 0) {
      result.aggs = this.aggregations;
    }

    // post_filter narrows hits without affecting aggregations
    if (this.postFilters.length > 0) {
      result.post_filter = { bool: { filter: this.postFilters } };
    }

    return result;
  }
}

/** Coarse classification of a single search token for type-routed search. */
export type SearchTokenKind = 'email' | 'identifier' | 'name';

/**
 * Classify a search token so the query can match it precisely instead of
 * fuzzily across every field:
 * - contains `@`        → email
 * - contains any digit  → identifier (payroll / employee id)
 * - otherwise           → name (typo-tolerant)
 *
 * Heuristic: a name containing a digit is rare and acceptably degrades to
 * identifier matching.
 */
export function classifySearchToken(token: string): SearchTokenKind {
  if (token.includes('@')) return 'email';
  if (/\d/.test(token)) return 'identifier';
  return 'name';
}

type AnyObj = Record<string, unknown>;
type HLMap = Record<string, string[] | string>;

export function highlight(
  item: AnyObj,
  path: string,
  opts?: { suffix?: string; join?: string }
): string {
  const suffix = opts?.suffix ?? '.analyzed';
  const joiner = opts?.join ?? ' ';

  const hl = (item as { highlight?: HLMap }).highlight;
  const raw = hl?.[`${path}${suffix}`] ?? hl?.[path];

  const text = Array.isArray(raw) ? raw.join(joiner) : typeof raw === 'string' ? raw : item[path];

  return text == null ? '' : String(text);
}
